import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import ReactMarkdown from 'react-markdown';
import { Modal as AntModal, Input, Spin, notification } from 'antd';
import {
  SendOutlined,
  AudioOutlined,
  StopOutlined,
  PlusOutlined,
  DeleteOutlined,
  MessageOutlined,
  FilePdfOutlined,
  EditOutlined,
} from '@ant-design/icons';
import EvaluationDetails from './EvaluationDetails';
import JobDescriptionModal from './JobDescriptionModal';
import { t } from '../../i18n/i18n';
import { AUDIO_CONFIG } from '../../constants/chatAssistant.constants';
import { CSS_CLASSES } from '../../constants/chat.constants';
import { useChatDraftManagement } from './hooks/useChatDraftManagement';
import { useMessageManagement } from './hooks/useMessageManagement';
import { useEvaluationManagement } from './hooks/useEvaluationManagement';
import { updateChatAssistantDraft } from '../../API/services/screeningService';
import ExperimentalFeatureBanner from '../../components/ExperimentalFeatureBanner/ExperimentalFeatureBanner';
import './Chat.scss';

const ProgressBar = ({ percentage }) => (
  <div className={CSS_CLASSES.PROGRESS_CONTAINER}>
    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
      <div style={{ flex: 1 }}>
        <div className={CSS_CLASSES.PROGRESS_BAR}>
          <div className={CSS_CLASSES.PROGRESS_FILL} style={{ width: `${percentage}%` }} />
        </div>
      </div>
      <span style={{ fontSize: '14px', fontWeight: '500', color: '#374151' }}>{percentage}%</span>
    </div>
  </div>
);

const Message = ({ message, isUser }) => {
  const displayContent = message.content;

  return (
    <div
      className={`${CSS_CLASSES.MESSAGE} ${
        isUser ? CSS_CLASSES.MESSAGE_USER : CSS_CLASSES.MESSAGE_ASSISTANT
      }`}
    >
      <div className={CSS_CLASSES.MESSAGE_CONTENT}>
        <div className={CSS_CLASSES.MESSAGE_BUBBLE}>
          {isUser ? (
            <div style={{ whiteSpace: 'pre-wrap' }}>{displayContent}</div>
          ) : (
            <div className="markdown-content">
              <ReactMarkdown
                renderers={{
                  heading: ({ level, children }) => {
                    const HeadingTag = `h${level}`;
                    const sizes = {
                      1: '1.5em',
                      2: '1.3em',
                      3: '1.1em',
                      4: '1em',
                      5: '0.9em',
                      6: '0.85em',
                    };
                    return (
                      <HeadingTag
                        style={{
                          fontSize: sizes[level],
                          marginTop: '0.5em',
                          marginBottom: '0.5em',
                        }}
                      >
                        {children}
                      </HeadingTag>
                    );
                  },
                  paragraph: ({ children }) => (
                    <p style={{ marginBottom: '0.8em', lineHeight: '1.6' }}>{children}</p>
                  ),
                  list: ({ ordered, children }) => {
                    const ListTag = ordered ? 'ol' : 'ul';
                    return (
                      <ListTag style={{ marginLeft: '1.5em', marginBottom: '0.8em' }}>
                        {children}
                      </ListTag>
                    );
                  },
                  listItem: ({ children }) => (
                    <li style={{ marginBottom: '0.3em', lineHeight: '1.5' }}>{children}</li>
                  ),
                  code: ({ value }) => (
                    <pre
                      style={{
                        backgroundColor: 'rgba(0,0,0,0.08)',
                        padding: '10px',
                        borderRadius: '5px',
                        overflowX: 'auto',
                        marginBottom: '0.8em',
                      }}
                    >
                      <code>{value}</code>
                    </pre>
                  ),
                  inlineCode: ({ children }) => (
                    <code
                      style={{
                        backgroundColor: 'rgba(0,0,0,0.08)',
                        padding: '2px 4px',
                        borderRadius: '3px',
                        fontSize: '0.9em',
                      }}
                    >
                      {children}
                    </code>
                  ),
                  strong: ({ children }) => (
                    <strong style={{ fontWeight: '600' }}>{children}</strong>
                  ),
                  emphasis: ({ children }) => <em style={{ fontStyle: 'italic' }}>{children}</em>,
                  blockquote: ({ children }) => (
                    <blockquote
                      style={{
                        borderLeft: '3px solid #ccc',
                        paddingLeft: '1em',
                        marginLeft: '0',
                        marginBottom: '0.8em',
                        fontStyle: 'italic',
                      }}
                    >
                      {children}
                    </blockquote>
                  ),
                  link: ({ href, children }) => (
                    <a
                      href={href}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        color: '#2563eb',
                        textDecoration: 'underline',
                      }}
                    >
                      {children}
                    </a>
                  ),
                }}
              >
                {displayContent}
              </ReactMarkdown>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const TypingIndicator = ({ message }) => {
  const rotatingMessage = useRotatingMessages();
  const displayMessage = message || rotatingMessage;

  return (
    <div className={`${CSS_CLASSES.MESSAGE} ${CSS_CLASSES.MESSAGE_ASSISTANT}`}>
      <div className={CSS_CLASSES.MESSAGE_CONTENT}>
        <div className={`${CSS_CLASSES.MESSAGE_BUBBLE} ${CSS_CLASSES.TYPING_BUBBLE}`}>
          <div className={CSS_CLASSES.TYPING_DOTS}>
            <span />
            <span />
            <span />
          </div>
          <span className={`${CSS_CLASSES.TYPING_TEXT} rotating-text`} key={displayMessage}>
            {displayMessage}
          </span>
        </div>
      </div>
    </div>
  );
};

const useRotatingMessages = () => {
  const [currentMessage, setCurrentMessage] = useState('');
  const [elapsedTime, setElapsedTime] = useState(0);

  const loadingMessages = useMemo(() => t('CHAT_LOADING_MESSAGES').split('|'), []);
  const patienceMessages = useMemo(() => t('CHAT_PATIENCE_MESSAGES').split('|'), []);

  useEffect(() => {
    const getRandomMessage = (isPatience) => {
      const messages = isPatience ? patienceMessages : loadingMessages;
      const randomIndex = Math.floor(Math.random() * messages.length);
      return messages[randomIndex];
    };

    setCurrentMessage(getRandomMessage(false));

    const timeInterval = setInterval(() => {
      setElapsedTime((prev) => prev + 3000);
    }, 3000);

    const messageInterval = setInterval(() => {
      setElapsedTime((currentElapsed) => {
        const isPatience = currentElapsed >= 12000;
        setCurrentMessage(getRandomMessage(isPatience));
        return currentElapsed;
      });
    }, 3000);

    return () => {
      clearInterval(timeInterval);
      clearInterval(messageInterval);
    };
  }, [loadingMessages, patienceMessages]);

  return currentMessage;
};

const useAudioRecorder = (onAudioReady) => {
  const [isRecording, setIsRecording] = useState(false);
  const [mediaRecorder, setMediaRecorder] = useState(null);

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream);
      const chunks = [];

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunks.push(e.data);
        }
      };

      recorder.onstop = () => {
        const blob = new Blob(chunks, { type: AUDIO_CONFIG.AUDIO_TYPE });
        stream.getTracks().forEach((track) => track.stop());

        if (onAudioReady) {
          const audioFile = new File([blob], AUDIO_CONFIG.AUDIO_FILENAME, {
            type: AUDIO_CONFIG.AUDIO_TYPE,
          });
          onAudioReady(audioFile);
        }
      };

      recorder.start();
      setMediaRecorder(recorder);
      setIsRecording(true);
    } catch (error) {
      notification.error({
        message: t('ERROR_MICROPHONE_ACCESS_TITLE'),
        description: t('ERROR_MICROPHONE_ACCESS'),
      });
    }
  };

  const stopRecording = () => {
    if (mediaRecorder && isRecording) {
      mediaRecorder.stop();
      setIsRecording(false);
    }
  };

  return {
    isRecording,
    startRecording,
    stopRecording,
  };
};

const Chat = () => {
  const history = useHistory();
  const [isCreatingNewChat, setIsCreatingNewChat] = useState(false);
  const [editingDraftId, setEditingDraftId] = useState(null);
  const [editingDraftName, setEditingDraftName] = useState('');
  const [showJobDescriptionModal, setShowJobDescriptionModal] = useState(false);
  const [currentDraftData, setCurrentDraftData] = useState(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const { currentWorkspace } = useSelector((state) => state.auth);
  const {
    chatDrafts,
    currentDraftId,
    loadingDrafts,
    showDeleteModal,
    draftToDelete,
    isDeletingDraft,
    isLoadingDraft,
    hasPendingSaves,
    setChatDrafts,
    setCurrentDraftId,
    setShowDeleteModal,
    setDraftToDelete,
    setIsLoadingDraft,
    loadChatDrafts,
    createNewDraft,
    handleDeleteDraftClick,
    deleteDraft,
    loadDraftWithMessages,
    addPendingSavePromise,
    waitForPendingSaves,
  } = useChatDraftManagement();

  const {
    messages,
    input,
    isLoading,
    threadId,
    operationMessage,
    setMessages,
    setInput,
    setThreadId,
    sendMessage,
    sendAudioMessage,
    resetMessages,
  } = useMessageManagement(addPendingSavePromise);

  const {
    evaluationData,
    percentage,
    isCreatingEvaluation,
    isEvaluationReady,
    updateEvaluationState,
    formatMessagesForDisplay,
    handleUpdateEvaluation,
    handleCreateEvaluation,
    resetEvaluationData,
    loadEvaluationFromDraft,
  } = useEvaluationManagement(addPendingSavePromise);

  const handleEditDraftName = useCallback(
    async (draftId, newName) => {
      if (!newName.trim()) return;

      try {
        await updateChatAssistantDraft(draftId, { name: newName.trim() });
        const updatedDrafts = chatDrafts.map((draft) =>
          draft.id === draftId ? { ...draft, name: newName.trim() } : draft
        );
        setChatDrafts(updatedDrafts);
        setEditingDraftId(null);
        setEditingDraftName('');
      } catch (error) {
        notification.error({
          message: t('ERROR'),
          description: error.message,
        });
      }
    },
    [chatDrafts, setChatDrafts]
  );

  const handleStartEditDraftName = useCallback((e, draft) => {
    e.stopPropagation();
    setEditingDraftId(draft.id);
    setEditingDraftName(draft.name);
  }, []);

  const handleDraftDeletion = useCallback(
    (draftId) => {
      if (currentDraftId === draftId) {
        setCurrentDraftId(null);
        setThreadId(null);
        resetMessages();
        resetEvaluationData();
      }
    },
    [currentDraftId, setThreadId, resetMessages, resetEvaluationData]
  );

  const loadDraft = useCallback(
    async (draft) => {
      if (currentDraftId === draft.id || isLoadingDraft || isLoading) {
        return;
      }

      setIsLoadingDraft(true);

      await waitForPendingSaves();

      if (isCreatingNewChat) {
        setIsCreatingNewChat(false);
      }

      setCurrentDraftId(draft.id);
      setThreadId(draft.threadId);
      resetEvaluationData();

      if (draft.id && draft.threadId) {
        try {
          const draftData = await loadDraftWithMessages(draft.id);

          setCurrentDraftData({
            id: draft.id,
            evaluationOpened: draftData.evaluationOpened || false,
            screeningId: draftData.screeningId || null,
          });

          if (draftData.messages && draftData.messages.length > 0) {
            const formattedMessages = formatMessagesForDisplay(draftData.messages);
            setMessages(formattedMessages);
          } else {
            setMessages([]);
          }

          loadEvaluationFromDraft({
            ...draftData,
            percentage: draftData.percentage ?? draft.percentage,
            name: draft.name,
            language: draft.language,
            duration: draft.duration,
            purpose: draft.purpose,
            startDate: draft.startDate,
            endDate: draft.endDate,
          });
        } catch (error) {
          setCurrentDraftData({
            id: draft.id,
            evaluationOpened: false,
            screeningId: null,
          });
          setMessages([]);
        } finally {
          setIsLoadingDraft(false);
        }
      } else if (draft.id && !draft.threadId) {
        setCurrentDraftData({
          id: draft.id,
          evaluationOpened: draft.evaluationOpened || false,
          screeningId: draft.screeningId || null,
        });
        setMessages([]);
        setIsLoadingDraft(false);
      }
    },
    [
      currentDraftId,
      isCreatingNewChat,
      isLoadingDraft,
      isLoading,
      setCurrentDraftId,
      setThreadId,
      setIsLoadingDraft,
      resetEvaluationData,
      loadDraftWithMessages,
      formatMessagesForDisplay,
      setMessages,
      loadEvaluationFromDraft,
      waitForPendingSaves,
    ]
  );

  const handleAudioReady = useCallback(
    async (audioFile) => {
      const result = await sendAudioMessage(
        audioFile,
        currentDraftId,
        currentDraftData,
        createNewDraft,
        updateEvaluationState
      );
      if (result && result.draftId && !currentDraftId) {
        setCurrentDraftId(result.draftId);
        setIsCreatingNewChat(false);
      }
    },
    [
      sendAudioMessage,
      currentDraftId,
      currentDraftData,
      createNewDraft,
      updateEvaluationState,
      setCurrentDraftId,
      setIsCreatingNewChat,
    ]
  );

  const { isRecording, startRecording, stopRecording } = useAudioRecorder(handleAudioReady);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (!isLoading && inputRef.current && !currentDraftData?.evaluationOpened) {
      inputRef.current.focus();
    }
  }, [isLoading, currentDraftData?.evaluationOpened]);

  useEffect(() => {
    const loadDraftsAndSelectLast = async () => {
      if (currentWorkspace) {
        await loadChatDrafts();
      }
    };
    loadDraftsAndSelectLast();
  }, [currentWorkspace]);

  useEffect(() => {
    if (
      chatDrafts.length > 0 &&
      !currentDraftId &&
      !loadingDrafts &&
      !threadId &&
      !isCreatingNewChat
    ) {
      const mostRecentDraft = chatDrafts[0];
      loadDraft(mostRecentDraft);
    }
  }, [chatDrafts, loadingDrafts, currentDraftId, threadId, loadDraft, isCreatingNewChat]);

  const handleInputChange = (e) => {
    setInput(e.target.value);
  };

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      if (input.trim()) {
        const result = await sendMessage(
          input,
          currentDraftId,
          currentDraftData,
          createNewDraft,
          updateEvaluationState
        );
        if (result && result.draftId && !currentDraftId) {
          setCurrentDraftId(result.draftId);
          setCurrentDraftData({
            id: result.draftId,
            evaluationOpened: false,
            screeningId: null,
          });
          setIsCreatingNewChat(false);
        }
      }
    },
    [
      input,
      sendMessage,
      currentDraftId,
      currentDraftData,
      createNewDraft,
      updateEvaluationState,
      setCurrentDraftId,
      setIsCreatingNewChat,
    ]
  );

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  const handleJobDescriptionSend = useCallback(
    async (extractedText) => {
      const jobDescriptionMessage = t('CHAT_JOB_DESCRIPTION_USER_MESSAGE');
      const fullMessage = `${jobDescriptionMessage}\n\n${t(
        'CHAT_JOB_DESCRIPTION_CONTENT'
      )}:\n${extractedText}`;

      const result = await sendMessage(
        fullMessage,
        currentDraftId,
        currentDraftData,
        createNewDraft,
        updateEvaluationState
      );

      if (result && result.draftId && !currentDraftId) {
        setCurrentDraftId(result.draftId);
        setIsCreatingNewChat(false);
      }
    },
    [
      sendMessage,
      currentDraftId,
      currentDraftData,
      createNewDraft,
      updateEvaluationState,
      setCurrentDraftId,
      setIsCreatingNewChat,
    ]
  );

  const getWelcomeMessage = () => (
    <>
      <p>{t('WELCOME_HELLO')}</p>
      <p>{t('WELCOME_READY_CREATE')}</p>
      <br />
      <p>{t('WELCOME_TELL_ROLE')}</p>
      <p>{t('WELCOME_OTHER_LANGUAGE')}</p>
    </>
  );

  return (
    <div className="chat-page-wrapper">
      <ExperimentalFeatureBanner />
      <div className={`${CSS_CLASSES.CHAT_CONTAINER} d-flex`}>
        <div className={`${CSS_CLASSES.CHAT_SIDEBAR} d-flex flex-column h-100`}>
        <div className={CSS_CLASSES.SIDEBAR_HEADER}>
          <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#1f2937' }}>
            {t('CHAT_DRAFTS_TITLE')}
          </h3>
          <button
            type="button"
            onClick={async () => {
              setIsCreatingNewChat(true);
              setThreadId(null);
              setCurrentDraftId(null);
              setCurrentDraftData(null);
              resetMessages();
              resetEvaluationData();
            }}
            className="new-chat-button"
            disabled={!currentDraftId || isLoading || hasPendingSaves || isLoadingDraft}
            style={{
              opacity: !currentDraftId || isLoading || hasPendingSaves || isLoadingDraft ? 0.5 : 1,
              cursor:
                !currentDraftId || isLoading || hasPendingSaves || isLoadingDraft
                  ? 'not-allowed'
                  : 'pointer',
            }}
            title={
              !currentDraftId
                ? t('CHAT_ALREADY_IN_NEW_CHAT')
                : isLoading || hasPendingSaves || isLoadingDraft
                ? t('CHAT_WAIT_OPERATION')
                : ''
            }
          >
            <PlusOutlined
              style={{
                fontSize: '16px',
                display: 'inline-block',
                marginRight: '4px',
                transition: 'transform 0.3s',
              }}
            />
            {t('CHAT_NEW_CHAT')}
          </button>
        </div>

        <div className="drafts-list">
          {loadingDrafts ? (
            <div
              className="loading-drafts"
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '32px 0',
              }}
            >
              <div
                style={{
                  animation: 'spin 1s linear infinite',
                  borderRadius: '50%',
                  height: '32px',
                  width: '32px',
                  borderBottom: '2px solid #2563eb',
                  borderTop: '2px solid transparent',
                  borderLeft: '2px solid transparent',
                  borderRight: '2px solid transparent',
                }}
              />
            </div>
          ) : chatDrafts.length === 0 ? (
            <div
              className="no-drafts"
              style={{ color: '#6b7280', textAlign: 'center', padding: '32px 0' }}
            >
              <MessageOutlined
                style={{
                  fontSize: '48px',
                  margin: '0 auto 8px',
                  color: '#d1d5db',
                }}
              />
              <p>{t('CHAT_NO_DRAFTS')}</p>
            </div>
          ) : (
            chatDrafts.map((draft) => (
              <div
                key={draft.id}
                role="button"
                tabIndex={0}
                className={`draft-item ${currentDraftId === draft.id ? 'active' : ''} ${
                  draft.evaluationOpened ? 'evaluation-opened' : ''
                } ${isLoading || hasPendingSaves || isLoadingDraft ? 'disabled' : ''}`}
                onClick={() => {
                  if (!isLoading && !hasPendingSaves && !isLoadingDraft) {
                    loadDraft(draft);
                  }
                }}
                onKeyPress={(e) => {
                  if (e.key === 'Enter' && !isLoading && !hasPendingSaves && !isLoadingDraft) {
                    loadDraft(draft);
                  }
                }}
                style={{
                  opacity: isLoading || hasPendingSaves || isLoadingDraft ? 0.6 : 1,
                  cursor:
                    isLoading || hasPendingSaves || isLoadingDraft ? 'not-allowed' : 'pointer',
                }}
                title={
                  isLoading || hasPendingSaves || isLoadingDraft ? t('CHAT_WAIT_OPERATION') : ''
                }
              >
                {currentDraftId === draft.id && hasPendingSaves && (
                  <div
                    className="spinner-border spinner-border-sm text-primary position-absolute"
                    style={{ top: '50%', right: '10px', transform: 'translateY(-50%)' }}
                    role="status"
                  >
                    <span className="sr-only">{t('CHAT_SAVING')}</span>
                  </div>
                )}
                <div style={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
                  {draft.evaluationOpened && (
                    <div
                      style={{
                        display: 'inline-block',
                        padding: '0.125rem 0.5rem',
                        backgroundColor: '#10b981',
                        color: 'white',
                        fontSize: '0.625rem',
                        borderRadius: 0,
                        marginBottom: '0.25rem',
                        fontWeight: '600',
                      }}
                    >
                      {t('CHAT_EVALUATION_OPENED')}
                    </div>
                  )}
                  {editingDraftId === draft.id ? (
                    <Input
                      value={editingDraftName}
                      onChange={(e) => {
                        const newValue = e.target.value;
                        if (newValue.length <= 60) {
                          setEditingDraftName(newValue);
                        }
                      }}
                      onPressEnter={() => handleEditDraftName(draft.id, editingDraftName)}
                      onBlur={() => handleEditDraftName(draft.id, editingDraftName)}
                      onClick={(e) => e.stopPropagation()}
                      autoFocus
                      size="small"
                      className="mr-2"
                      style={{ marginBottom: '0.25rem', width: '100%' }}
                      maxLength={60}
                    />
                  ) : (
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        overflow: 'hidden',
                      }}
                    >
                      <div
                        className="draft-name"
                        onDoubleClick={(e) => handleStartEditDraftName(e, draft)}
                        style={{
                          cursor: 'text',
                          flex: '1 1 auto',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          minWidth: 0,
                        }}
                        title={draft.name}
                      >
                        {draft.name}
                      </div>
                      <button
                        type="button"
                        onClick={(e) => handleStartEditDraftName(e, draft)}
                        style={{
                          background: 'transparent',
                          border: 'none',
                          cursor: 'pointer',
                          padding: '4px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          opacity: 0.7,
                          transition: 'opacity 0.2s',
                          flexShrink: 0,
                        }}
                        onMouseEnter={(e) => {
                          const target = e.currentTarget;
                          target.style.opacity = '1';
                        }}
                        onMouseLeave={(e) => {
                          const target = e.currentTarget;
                          target.style.opacity = '0.7';
                        }}
                        title={t('CHAT_EDIT_DRAFT_NAME') || 'Editar nombre'}
                      >
                        <EditOutlined style={{ fontSize: '14px', color: '#6b7280' }} />
                      </button>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteDraftClick(draft.id, draft.threadId);
                        }}
                        style={{
                          background: 'transparent',
                          border: 'none',
                          cursor: 'pointer',
                          padding: '4px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          opacity: 0.7,
                          transition: 'opacity 0.2s',
                          flexShrink: 0,
                        }}
                        onMouseEnter={(e) => {
                          const target = e.currentTarget;
                          target.style.opacity = '1';
                        }}
                        onMouseLeave={(e) => {
                          const target = e.currentTarget;
                          target.style.opacity = '0.7';
                        }}
                        title={t('CHAT_DELETE_DRAFT')}
                      >
                        <DeleteOutlined
                          style={{
                            fontSize: '14px',
                            color: '#ef4444',
                          }}
                        />
                      </button>
                    </div>
                  )}
                  <div className="draft-date">
                    {draft.createdAt
                      ? new Date(draft.createdAt).toLocaleDateString('es-ES')
                      : draft.startDate
                      ? new Date(draft.startDate).toLocaleDateString('es-ES')
                      : t('CHAT_NO_DATE')}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      <div className="chat-main d-flex flex-column h-100">
        <ProgressBar percentage={percentage} />

        <div className="chat-messages flex-fill overflow-auto">
          {messages.length === 0 && (
            <div className="welcome-message fade-in">
              <div className="welcome-text">{getWelcomeMessage()}</div>
            </div>
          )}

          {messages.map((message) => (
            <Message key={message.id} message={message} isUser={message.role === 'user'} />
          ))}

          {isLoading && <TypingIndicator message={operationMessage} />}
          <div ref={messagesEndRef} />
        </div>

        <div className="chat-input-area">
          {currentDraftData?.evaluationOpened ? (
            <div className="p-3 bg-light rounded d-flex align-items-center justify-content-between">
              <div>
                <div className="font-weight-bold text-dark mb-1">{t('CHAT_EVALUATION_OPENED')}</div>
                <div className="text-muted small">{t('CHAT_EVALUATION_OPENED_READONLY')}</div>
              </div>
              {currentDraftData?.screeningId && (
                <button
                  type="button"
                  onClick={() => {
                    history.push(
                      `/search/${currentDraftData.screeningId}?workspaceId=${currentWorkspace}`
                    );
                  }}
                  className="px-4 py-2"
                  style={{
                    backgroundColor: '#4987be',
                    color: 'white',
                    borderRadius: 0,
                    textDecoration: 'none',
                    fontWeight: '500',
                    fontSize: '0.875rem',
                    border: 'none',
                    cursor: 'pointer',
                  }}
                >
                  {t('CHAT_VIEW_EVALUATION')}
                </button>
              )}
            </div>
          ) : (
            <>
              <form onSubmit={handleSubmit} className="chat-input-form">
                <div className="input-container d-flex align-items-center">
                  <input
                    ref={inputRef}
                    type="text"
                    value={input}
                    onChange={handleInputChange}
                    placeholder={t('CHAT_TYPE_MESSAGE')}
                    disabled={isLoading || currentDraftData?.evaluationOpened}
                    onKeyPress={handleKeyPress}
                    className="message-input flex-fill"
                  />
                  <button
                    type="button"
                    onClick={isRecording ? stopRecording : startRecording}
                    disabled={isLoading}
                    className={`mic-button ${isRecording ? 'recording' : ''}`}
                    title={isRecording ? t('CHAT_STOP_RECORDING') : t('CHAT_RECORD_AUDIO')}
                  >
                    {isRecording ? (
                      <StopOutlined style={{ fontSize: '20px' }} />
                    ) : (
                      <AudioOutlined style={{ fontSize: '20px' }} />
                    )}
                  </button>
                  <button
                    type="submit"
                    disabled={isLoading || !input.trim()}
                    className="send-button"
                    title="Enviar mensaje"
                  >
                    {isLoading ? (
                      <div
                        style={{
                          animation: 'spin 1s linear infinite',
                          borderRadius: '50%',
                          height: '20px',
                          width: '20px',
                          borderBottom: '2px solid white',
                          borderTop: '2px solid transparent',
                          borderLeft: '2px solid transparent',
                          borderRight: '2px solid transparent',
                        }}
                      />
                    ) : (
                      <SendOutlined style={{ fontSize: '20px' }} />
                    )}
                  </button>
                </div>
              </form>

              <button
                type="button"
                className="message-input job-description-button"
                onClick={() => setShowJobDescriptionModal(true)}
                disabled={isLoading || currentDraftData?.evaluationOpened}
                style={{
                  marginTop: '0.5rem',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <FilePdfOutlined style={{ marginRight: '0.5rem', fontSize: '1.125rem' }} />
                {t('CHAT_JOB_DESCRIPTION_BUTTON')}
              </button>
            </>
          )}
        </div>
      </div>

      <div className="evaluation-sidebar d-flex flex-column h-100">
        <EvaluationDetails
          evaluationData={evaluationData}
          isEvaluationReady={isEvaluationReady}
          onCreateEvaluation={handleCreateEvaluation}
          onUpdateEvaluation={handleUpdateEvaluation}
          isCreatingEvaluation={isCreatingEvaluation}
          currentDraftId={currentDraftId}
          currentDraftData={currentDraftData}
        />
      </div>

      <AntModal
        title={t('DELETE_DRAFT_THE_OWNER_MODAL_TITLE')}
        open={showDeleteModal}
        onCancel={() => {
          setShowDeleteModal(false);
          setDraftToDelete(null);
        }}
        footer={
          <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
            <button
              key="cancel"
              type="button"
              className="px-5 py-3 modal-buttons-border bg-transparent dont-delete-draft-button-border border-red-danger text-red-danger outline-none text-xs font-weight-700"
              onClick={() => {
                setShowDeleteModal(false);
                setDraftToDelete(null);
              }}
              disabled={isDeletingDraft}
              style={{ opacity: isDeletingDraft ? 0.6 : 1 }}
            >
              {t('DELETE_DRAFT_THE_OWNER_MODAL_NO_BUTTON')}
            </button>
            <button
              key="delete"
              type="button"
              className="px-5 py-3 modal-buttons-border bg-red-danger border-0 text-white outline-none text-xs font-weight-700"
              onClick={async () => {
                if (draftToDelete) {
                  await deleteDraft(draftToDelete.id, handleDraftDeletion);
                }
              }}
              disabled={isDeletingDraft}
              style={{
                opacity: isDeletingDraft ? 0.7 : 1,
                minWidth: '6rem',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {isDeletingDraft ? (
                <Spin size="small" style={{ color: 'white' }} />
              ) : (
                t('DELETE_DRAFT_THE_OWNER_MODAL_YES_BUTTON')
              )}
            </button>
          </div>
        }
        width={400}
        centered
      >
        <p>{t('DELETE_DRAFT_THE_OWNER_MODAL_SUBTITLE')}</p>
      </AntModal>

      <JobDescriptionModal
        visible={showJobDescriptionModal}
        onClose={() => setShowJobDescriptionModal(false)}
        onSendToAssistant={handleJobDescriptionSend}
      />
      </div>
    </div>
  );
};

export default Chat;
